// Saves an output matrix to the client workspace as a staff plan.
//
// Shared by the Modeled matrix and the Final Staff Plan card. Both save a
// generated plan, which is kept out of the Actual slot a scenario uses for cost
// savings — so this records a proposal, not a new baseline. Each caller supplies
// its own explanatory note, since what the saved plan means differs.
//
// Once a matrix has been saved, the same matrix re-saved is usually the same
// proposal with newer numbers rather than a second proposal, so the panel offers
// updating that plan in place as well as saving a new one. Which plan "in place"
// refers to is resolved by the page that owns the client's plan list.

import { useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { Check, Save } from 'lucide-react';
import type { PublishTarget } from './publishPlan';
import { fmtRelative } from '../../lib/format';

export default function PublishPlanButton({
  defaultName,
  note,
  onPublish,
  existing,
  title = 'Save this grid to the client workspace as a staff plan',
}: {
  defaultName: string;
  /** Explains what the saved plan is and how it may be used. */
  note: ReactNode;
  /** `replaceId` overwrites that plan; without it a new plan is created. */
  onPublish: (name: string, replaceId?: string) => Promise<void>;
  /** The plan this matrix has already been saved to, when there is one. */
  existing?: PublishTarget;
  title?: string;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(defaultName);
  const [busy, setBusy] = useState<'update' | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const run = async (mode: 'update' | 'new') => {
    if (busy) return;
    const trimmed = mode === 'update' ? existing?.name || name.trim() : name.trim();
    if (!trimmed) return;
    setBusy(mode);
    setError(null);
    try {
      await onPublish(trimmed, mode === 'update' ? existing?.id : undefined);
      setDone(true);
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the plan');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          setName(defaultName);
          setDone(false);
          setError(null);
          setOpen((v) => !v);
        }}
        className="btn-secondary text-xs"
        title={title}
      >
        {done ? <Check size={13} /> : <Save size={13} />}
        {done ? 'Saved' : 'Save to workspace'}
      </button>
      {open && (
        <div className="absolute right-0 mt-1 z-30 w-[320px] bg-white border border-slate-200 rounded-lg shadow-lg p-3">
          {existing && (
            <div className="mb-3 pb-3 border-b border-slate-100">
              <button
                type="button"
                onClick={() => run('update')}
                disabled={!!busy}
                className="btn-primary text-xs w-full justify-center disabled:opacity-50"
                title={`Replace the numbers on "${existing.name}" with what is on screen`}
              >
                {busy === 'update' ? 'Updating…' : 'Save current'}
              </button>
              <p className="mt-1.5 text-[11px] text-slate-500">
                Updates{' '}
                <span className="font-medium text-slate-600">{existing.name}</span>
                {existing.updatedAt && <> — saved {fmtRelative(existing.updatedAt)}</>}
                . The plan keeps its name and its place in the workspace; only the
                numbers change.
              </p>
            </div>
          )}
          <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500">
            {existing ? 'Or save as a new plan' : 'Plan name'}
          </label>
          <input
            className="input mt-1 w-full text-xs"
            value={name}
            autoFocus={!existing}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') run('new');
              if (e.key === 'Escape') setOpen(false);
            }}
          />
          <p className="mt-2 text-[11px] text-slate-500">{note}</p>
          {error && <p className="mt-2 text-[11px] text-rose-600">{error}</p>}
          <div className="mt-2.5 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs text-slate-500 hover:text-slate-800"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => run('new')}
              disabled={!!busy || !name.trim()}
              className={clsx(
                'text-xs disabled:opacity-50',
                // Saving a new plan leads until there is something to update, at
                // which point updating is the likelier intent.
                existing ? 'btn-secondary' : 'btn-primary',
              )}
            >
              {busy === 'new' ? 'Saving…' : existing ? 'Save new' : 'Save plan'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
